import React, { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from '../layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Activity,
    Database,
    Mail,
    Cloud,
    Archive,
    Clock,
    HardDrive,
    CheckCircle2,
    AlertTriangle,
    XCircle,
    RefreshCcw,
    Loader2,
    BellRing,
    Settings as SettingsIcon,
    FileLock2,
    Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { healthApi } from '../../lib/api';
import api from '../../lib/api';

const COMPONENT_ICONS = {
    database: Database,
    smtp: Mail,
    dropbox: Cloud,
    auto_backup: Archive,
    scheduler: Clock,
    email_delivery: Mail,
    disk: HardDrive,
};

const STATUS_META = {
    ok: {
        label: 'OK',
        icon: CheckCircle2,
        badgeCls: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        accentCls: 'border-emerald-200 bg-emerald-50/40',
        iconCls: 'text-emerald-600',
    },
    warning: {
        label: 'Warnung',
        icon: AlertTriangle,
        badgeCls: 'bg-amber-100 text-amber-900 border-amber-200',
        accentCls: 'border-amber-200 bg-amber-50/50',
        iconCls: 'text-amber-600',
    },
    error: {
        label: 'Fehler',
        icon: XCircle,
        badgeCls: 'bg-rose-100 text-rose-800 border-rose-200',
        accentCls: 'border-rose-200 bg-rose-50/50',
        iconCls: 'text-rose-600',
    },
};

function formatIsoBerlin(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('de-DE', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return iso;
    }
}

function ComponentDetails({ comp }) {
    const { key, details } = comp;
    if (!details || Object.keys(details).length === 0) return null;
    if (key === 'scheduler' && Array.isArray(details.jobs)) {
        return (
            <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                {details.jobs.map(j => (
                    <li key={j.id} className="flex items-center justify-between gap-3 border-t border-border/50 pt-1">
                        <span className="font-mono text-foreground/80">{j.id}</span>
                        <span>nächster Lauf: {formatIsoBerlin(j.next_run)}</span>
                    </li>
                ))}
            </ul>
        );
    }
    if (key === 'email_delivery' && Array.isArray(details.recent_failures) && details.recent_failures.length > 0) {
        return (
            <div className="mt-3 text-xs">
                <p className="font-semibold text-muted-foreground mb-1">Letzte Fehlschläge:</p>
                <ul className="space-y-1">
                    {details.recent_failures.map((f, i) => (
                        <li key={i} className="border-t border-border/50 pt-1">
                            <span className="font-medium">{f.recipient_email}</span>
                            <span className="text-muted-foreground"> · {formatIsoBerlin(f.sent_at)}</span>
                            {f.error && <span className="block text-rose-700">{f.error}</span>}
                        </li>
                    ))}
                </ul>
            </div>
        );
    }
    if (key === 'auto_backup') {
        return (
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                {details.last_run_at && (<><dt>Letzter Lauf:</dt><dd>{formatIsoBerlin(details.last_run_at)}</dd></>)}
                {details.last_path && (<><dt>Pfad:</dt><dd className="break-all font-mono">{details.last_path}</dd></>)}
                {details.retention_days != null && (<><dt>Aufbewahrung:</dt><dd>{details.retention_days} Tage</dd></>)}
            </dl>
        );
    }
    if (key === 'database') {
        return (
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                <dt>Collections:</dt><dd>{details.collections}</dd>
                <dt>Objekte:</dt><dd>{details.objects?.toLocaleString('de-DE')}</dd>
                <dt>Daten:</dt><dd>{details.data_size_mb} MB</dd>
            </dl>
        );
    }
    if (key === 'smtp') {
        return (
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                <dt>Host:</dt><dd className="font-mono">{details.host}:{details.port}</dd>
                {details.from_email && (<><dt>Absender:</dt><dd>{details.from_email}</dd></>)}
            </dl>
        );
    }
    if (key === 'dropbox') {
        return (
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                {details.account_email && (<><dt>Konto:</dt><dd>{details.account_email}</dd></>)}
                {details.base_folder && (<><dt>Ordner:</dt><dd className="font-mono">{details.base_folder}</dd></>)}
            </dl>
        );
    }
    if (key === 'disk') {
        return (
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                <dt>Belegt:</dt><dd>{details.percent} %</dd>
                <dt>Frei:</dt><dd>{details.free_gb} GB von {details.total_gb} GB</dd>
            </dl>
        );
    }
    return null;
}

export default function SystemHealthPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [alertChecking, setAlertChecking] = useState(false);
    const [diskCfg, setDiskCfg] = useState(null);
    const [diskCfgSaving, setDiskCfgSaving] = useState(false);
    const [pdfProtection, setPdfProtection] = useState(null);
    const [pdfProtectionSaving, setPdfProtectionSaving] = useState(false);
    const [pdfTestBusy, setPdfTestBusy] = useState(false);
    const [repairBusy, setRepairBusy] = useState(false);

    const loadDiskCfg = useCallback(async () => {
        try {
            const res = await healthApi.getDiskConfig();
            setDiskCfg(res.data);
        } catch (e) {
            // ignore – Werte sind nicht kritisch fürs Dashboard
        }
    }, []);

    const loadPdfProtection = useCallback(async () => {
        try {
            const res = await api.get('/settings/pdf-protection');
            setPdfProtection(res.data);
        } catch (e) {
            // ignore – Endpoint existiert erst nach Deploy
        }
    }, []);

    const handleTogglePdfProtection = async (enabled) => {
        setPdfProtectionSaving(true);
        try {
            const res = await api.post('/settings/pdf-protection', { enabled });
            setPdfProtection({ enabled: res.data?.enabled });
            toast.success(res.data?.message || 'Gespeichert');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setPdfProtectionSaving(false);
        }
    };

    const handleDownloadTestPdf = async () => {
        setPdfTestBusy(true);
        try {
            const res = await api.get('/pdf/protection-test', { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'BGPR_Schreibschutz_Test.pdf';
            document.body.appendChild(a);
            a.click();
            URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('Test-PDF heruntergeladen — jetzt in Adobe Acrobat öffnen');
        } catch (e) {
            const detail = e.response?.data?.detail || e.message || 'Download fehlgeschlagen';
            toast.error(typeof detail === 'string' ? detail : 'Download fehlgeschlagen');
        } finally {
            setPdfTestBusy(false);
        }
    };

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        try {
            const res = await healthApi.detailed();
            setData(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Health-Status konnte nicht geladen werden');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        load();
        loadDiskCfg();
        loadPdfProtection();
        const id = setInterval(() => load(true), 60_000);
        return () => clearInterval(id);
    }, [load, loadDiskCfg, loadPdfProtection]);

    const handleSaveDiskCfg = async (patch) => {
        const next = { ...diskCfg, ...patch };
        setDiskCfg(next);
        setDiskCfgSaving(true);
        try {
            const res = await healthApi.saveDiskConfig(next);
            setDiskCfg(res.data?.data || next);
            toast.success('Festplatten-Überwachung gespeichert');
            // Sofort neu laden, damit die Karte den neuen Status spiegelt
            load(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setDiskCfgSaving(false);
        }
    };

    const handleRunAlertCheck = async () => {
        setAlertChecking(true);
        try {
            const res = await healthApi.runAlertCheck();
            if (res.data?.sent) {
                toast.success(`Alert-Mail versendet an ${res.data.recipient}`);
            } else {
                toast.info(`Keine Alert-Mail nötig: ${res.data?.reason || 'OK'}`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Alert-Check fehlgeschlagen');
        } finally {
            setAlertChecking(false);
        }
    };

    const overallMeta = data ? STATUS_META[data.overall] || STATUS_META.warning : null;
    const OverallIcon = overallMeta?.icon || Activity;

    return (
        <AdminLayout>
            <div className="space-y-6" data-testid="system-health-page">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                            <Activity className="h-7 w-7 text-primary" />
                            System-Health
                        </h1>
                        <p className="text-muted-foreground mt-1 text-sm">
                            Live-Status aller kritischen Infrastruktur-Komponenten. Aktualisiert sich automatisch alle 60 Sekunden.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => load(true)}
                            disabled={refreshing}
                            data-testid="health-refresh-btn"
                        >
                            {refreshing
                                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                : <RefreshCcw className="h-4 w-4 mr-2" />}
                            Aktualisieren
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleRunAlertCheck}
                            disabled={alertChecking}
                            data-testid="health-trigger-alert-btn"
                            title="Sendet sofort eine Alert-Mail an die Admin-Adresse, falls aktuell Fehler bestehen."
                        >
                            {alertChecking
                                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                : <BellRing className="h-4 w-4 mr-2" />}
                            Alert-Check ausführen
                        </Button>
                    </div>
                </div>

                {/* Overall status banner */}
                {loading && !data && (
                    <Card>
                        <CardContent className="py-12 flex items-center justify-center text-muted-foreground">
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Health-Status wird geladen…
                        </CardContent>
                    </Card>
                )}
                {data && (
                    <Card
                        className={`border-2 ${overallMeta.accentCls}`}
                        data-testid="health-overall-card"
                    >
                        <CardContent className="py-6">
                            <div className="flex items-center gap-4 flex-wrap">
                                <div className={`p-3 rounded-full bg-white ${overallMeta.iconCls}`}>
                                    <OverallIcon className="h-7 w-7" />
                                </div>
                                <div className="flex-1 min-w-[200px]">
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-xl font-semibold" data-testid="health-overall-status">
                                            {data.overall === 'ok' && 'Alles in Ordnung'}
                                            {data.overall === 'warning' && `${data.warning_count} Warnung${data.warning_count !== 1 ? 'en' : ''}`}
                                            {data.overall === 'error' && `${data.error_count} Fehler${data.error_count !== 1 ? '' : ''}`}
                                        </h2>
                                        <Badge variant="outline" className={overallMeta.badgeCls}>
                                            {overallMeta.label}
                                        </Badge>
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {data.components.length} Komponenten geprüft · Stand: {formatIsoBerlin(data.generated_at)}
                                    </p>
                                </div>
                                {data.warning_count + data.error_count > 0 && (
                                    <div className="flex gap-2 text-xs">
                                        {data.error_count > 0 && (
                                            <Badge className="bg-rose-100 text-rose-800 border border-rose-200">
                                                {data.error_count} Fehler
                                            </Badge>
                                        )}
                                        {data.warning_count > 0 && (
                                            <Badge className="bg-amber-100 text-amber-900 border border-amber-200">
                                                {data.warning_count} Warnungen
                                            </Badge>
                                        )}
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Component cards */}
                {data && (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {data.components.map(comp => {
                            const meta = STATUS_META[comp.status] || STATUS_META.warning;
                            const Icon = COMPONENT_ICONS[comp.key] || Activity;
                            const StatusIcon = meta.icon;
                            return (
                                <Card
                                    key={comp.key}
                                    className={`border ${meta.accentCls}`}
                                    data-testid={`health-card-${comp.key}`}
                                >
                                    <CardHeader className="pb-3">
                                        <div className="flex items-center justify-between gap-2">
                                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                                                <Icon className={`h-5 w-5 ${meta.iconCls}`} />
                                                {comp.name}
                                            </CardTitle>
                                            <Badge variant="outline" className={`text-xs ${meta.badgeCls}`}>
                                                <StatusIcon className="h-3 w-3 mr-1" />
                                                {meta.label}
                                            </Badge>
                                        </div>
                                        <CardDescription className="text-sm leading-snug mt-2">
                                            {comp.message}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="pt-0">
                                        <ComponentDetails comp={comp} />
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* Disk-Monitoring-Konfiguration */}
                {diskCfg && (
                    <Card data-testid="health-disk-config-card">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                                <SettingsIcon className="h-4 w-4 text-muted-foreground" />
                                Festplatten-Überwachung
                            </CardTitle>
                            <CardDescription>
                                Die geprüfte Platte gehört zum Hosting-Container (geteilter Speicher der
                                Emergent-Plattform) – darauf hat der Verein keinen direkten Einfluss.
                                Schwellwerte oder das Ausblenden der Karte lassen sich hier anpassen.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <Label htmlFor="disk-enabled" className="text-sm font-medium">
                                        Überwachung aktiv
                                    </Label>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Ausschalten, wenn dich die Plattform-Belegung nicht interessiert.
                                    </p>
                                </div>
                                <Switch
                                    id="disk-enabled"
                                    checked={!!diskCfg.enabled}
                                    disabled={diskCfgSaving}
                                    onCheckedChange={(v) => handleSaveDiskCfg({ enabled: !!v })}
                                    data-testid="disk-monitoring-toggle"
                                />
                            </div>
                            {diskCfg.enabled && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-border/60">
                                    <div>
                                        <Label htmlFor="disk-warn" className="text-xs font-medium text-muted-foreground">
                                            Warnung ab (%)
                                        </Label>
                                        <Input
                                            id="disk-warn"
                                            type="number"
                                            min="1"
                                            max="99"
                                            step="1"
                                            className="mt-1 h-9"
                                            value={diskCfg.warn_percent}
                                            disabled={diskCfgSaving}
                                            onChange={(e) => setDiskCfg({ ...diskCfg, warn_percent: Number(e.target.value) })}
                                            onBlur={() => handleSaveDiskCfg({ warn_percent: Number(diskCfg.warn_percent) })}
                                            data-testid="disk-warn-input"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="disk-error" className="text-xs font-medium text-muted-foreground">
                                            Fehler ab (%)
                                        </Label>
                                        <Input
                                            id="disk-error"
                                            type="number"
                                            min="1"
                                            max="99"
                                            step="1"
                                            className="mt-1 h-9"
                                            value={diskCfg.error_percent}
                                            disabled={diskCfgSaving}
                                            onChange={(e) => setDiskCfg({ ...diskCfg, error_percent: Number(e.target.value) })}
                                            onBlur={() => handleSaveDiskCfg({ error_percent: Number(diskCfg.error_percent) })}
                                            data-testid="disk-error-input"
                                        />
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* PDF-Schreibschutz (AES-256) */}
                {pdfProtection !== null && (
                    <Card data-testid="pdf-protection-card">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                                <FileLock2 className="h-4 w-4 text-muted-foreground" />
                                PDF-Schreibschutz (AES-256)
                            </CardTitle>
                            <CardDescription>
                                Versiegelt alle generierten Lizenz- und Bescheinigungs-PDFs mit
                                AES-256-Verschlüsselung (Adobe-Acrobat-Standard seit 2010). PDFs öffnen
                                ohne Passwort-Abfrage, sind aber gegen Bearbeitung geschützt.
                                Drucken und Text-Kopieren bleiben erlaubt.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900">
                                <strong>Vor dem Aktivieren:</strong> Zuerst mit dem Button unten eine
                                Test-PDF herunterladen und in <em>Adobe Acrobat</em> öffnen. Wenn Adobe
                                sie sauber öffnet (keine „beschädigt"-Warnung), dann den globalen
                                Schalter aktivieren.
                            </div>
                            <div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleDownloadTestPdf}
                                    disabled={pdfTestBusy}
                                    data-testid="pdf-protection-test-btn"
                                >
                                    {pdfTestBusy
                                        ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        : <Download className="h-4 w-4 mr-2" />}
                                    Test-PDF herunterladen
                                </Button>
                                <p className="text-xs text-muted-foreground mt-1.5">
                                    Ein Dummy-Zertifikat mit AES-256-Schutz — keine Änderung an
                                    Buchungen, Dropbox oder Lizenzen.
                                </p>
                            </div>
                            <div className="flex items-center justify-between gap-3 pt-2 border-t border-border/60">
                                <div>
                                    <Label htmlFor="pdf-protection-toggle" className="text-sm font-medium">
                                        Global aktiv
                                    </Label>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Wenn aktiv, werden ALLE neu erzeugten Lizenz- und
                                        Bescheinigungs-PDFs mit Schreibschutz versiegelt.
                                    </p>
                                </div>
                                <Switch
                                    id="pdf-protection-toggle"
                                    checked={!!pdfProtection.enabled}
                                    disabled={pdfProtectionSaving}
                                    onCheckedChange={(v) => handleTogglePdfProtection(!!v)}
                                    data-testid="pdf-protection-toggle"
                                />
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Verwendet-Marker Bulk-Reparatur (Bug Teil 55) */}
                <Card data-testid="verwendet-repair-card">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <RefreshCcw className="h-4 w-4 text-muted-foreground" />
                            Verwendet-Marker reparieren
                        </CardTitle>
                        <CardDescription>
                            Repariert alle Fortbildungsnachweise, deren Verwendung durch den früheren
                            Direkt-Verlängerungs-Flow inkonsistent gespeichert wurde. Betroffene
                            Portal-Widgets („Verfügbare Fortbildungs-UE") zeigen danach wieder
                            korrekte Zahlen für alle Mitglieder. Läuft automatisch bei jedem
                            Backend-Neustart – dieser Button ist der manuelle Trigger mit sofortigem
                            Report.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                                setRepairBusy(true);
                                try {
                                    const res = await api.post('/training-records/admin/repair-verwendet-marker');
                                    toast.success(res.data?.message || 'Reparatur ausgeführt');
                                } catch (e) {
                                    toast.error(e.response?.data?.detail || 'Reparatur fehlgeschlagen');
                                } finally {
                                    setRepairBusy(false);
                                }
                            }}
                            disabled={repairBusy}
                            data-testid="repair-verwendet-btn"
                        >
                            {repairBusy
                                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                : <RefreshCcw className="h-4 w-4 mr-2" />}
                            Jetzt reparieren
                        </Button>
                    </CardContent>
                </Card>

                {/* Footer info */}
                <Card className="bg-muted/30">
                    <CardContent className="py-4 text-xs text-muted-foreground space-y-1">
                        <p>
                            <strong>Automatisierter Alert:</strong> Stündlicher Hintergrund-Job prüft den Gesamt-Zustand.
                            Liegt ein <span className="font-semibold text-rose-700">Fehler</span> vor, wird eine Mail an die
                            unter Einstellungen → Mail → System-Benachrichtigungen hinterlegte Adresse gesendet
                            (Cooldown 24 h pro identischer Problem-Kombination).
                        </p>
                        <p>
                            <strong>Hinweis:</strong> Warnungen (z.B. „SMTP nicht konfiguriert") triggern <em>keine</em> automatische
                            Alert-Mail – nur echte Fehler. Über „Alert-Check ausführen" kann ein manueller Versand erzwungen werden.
                        </p>
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    );
}
